import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Award, Loader2, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TraderDetailPage = () => {
  const { certificateId } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const res = await axios.get(`${API}/graduates/${certificateId}`);
        setCertificate(res.data);
      } catch (err) {
        setError('Certificate not found');
      } finally {
        setLoading(false);
      }
    };
    fetchCertificate();
  }, [certificateId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-degen-purple animate-spin" />
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <Award className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="font-unbounded font-bold text-2xl text-white mb-2">Certificate Not Found</h2>
          <p className="text-zinc-400 mb-6">This degree could not be verified.</p>
          <Link to="/wall-of-traders" className="text-pump-green hover:underline">Back to Wall of Traders</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Back Link */}
        <Link
          to="/wall-of-traders"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-pump-green transition-colors mb-8"
          data-testid="back-to-wall"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back to Wall of Traders</span>
        </Link>

        {/* Verified Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 mb-6"
        >
          <CheckCircle className="text-pump-green" size={20} />
          <span className="text-pump-green text-sm font-medium font-mono">Verified Degree</span>
        </motion.div>

        {/* Certificate Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="rounded-2xl overflow-hidden border border-pump-green/20 shadow-[0_0_40px_rgba(57,255,20,0.1)] mb-8"
        >
          <div className="relative">
            <img
              src={certificate.certificate_image}
              alt={`${certificate.student_name}'s Certificate`}
              className="w-full h-auto"
              data-testid="certificate-image"
            />
            <div className="absolute inset-0 flex flex-col items-center pointer-events-none">
              <div className="absolute w-full text-center" style={{ top: '40%' }}>
                <p
                  className="font-serif italic text-gray-800 px-4"
                  style={{
                    fontSize: 'clamp(16px, 4vw, 32px)',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    letterSpacing: '2px',
                    fontWeight: '500'
                  }}
                >
                  {certificate.student_name}
                </p>
              </div>
              <div className="absolute w-full text-center" style={{ top: '54%' }}>
                <p
                  className="font-serif italic text-gray-800"
                  style={{
                    fontSize: 'clamp(14px, 3vw, 26px)',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    letterSpacing: '1px',
                    fontWeight: '500'
                  }}
                >
                  {certificate.gpa?.toFixed(2)}
                </p>
              </div>
              <div className="absolute" style={{ top: '63.5%', left: '58%' }}>
                <p
                  className="font-serif text-gray-700"
                  style={{
                    fontSize: 'clamp(10px, 1.8vw, 16px)',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontStyle: 'italic'
                  }}
                >
                  {certificate.issue_date && new Date(certificate.issue_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Detail Card — View Only, No Share */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-2xl p-6 space-y-4"
          data-testid="certificate-detail-card"
        >
          <div className="flex items-center gap-3 mb-2">
            <Award className="text-degen-purple" size={24} />
            <h2 className="font-unbounded font-bold text-xl text-white">{certificate.degree}</h2>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-zinc-400">Student</span>
            <span className="font-bold text-white">{certificate.student_name}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-zinc-400">Certificate ID</span>
            <span className="font-mono text-pump-green">{certificate.certificate_number}</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-zinc-400">GPA</span>
            <span className="font-bold text-xl text-white">{certificate.gpa?.toFixed(2)} / 4.00</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-zinc-400">Issued</span>
            <span className="text-white">
              {certificate.issue_date && new Date(certificate.issue_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>

          {/* Course Breakdown */}
          {certificate.course_grades && (
            <div className="pt-4 border-t border-white/10">
              <p className="text-sm text-zinc-500 mb-3">Course Breakdown</p>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(certificate.course_grades).map(([course, grades]) => (
                  <div key={course} className="bg-pump-surface rounded-lg p-3">
                    <p className="text-xs text-zinc-500 uppercase">{course}</p>
                    <p className="text-white font-medium">{grades.score}/{grades.total} ({grades.percentage}%)</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default TraderDetailPage;
